import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Clock, Users, Copy, Star, ArrowLeft, MessageSquare, Check, Play, Pause, SkipForward, SkipBack, Volume2, VolumeX, Utensils, X, Loader2, ShoppingCart, Share2, Download, Camera, PartyPopper, Trophy, Heart, FileText, Edit2, Wine, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import ChefAvatar from "@/components/ChefAvatar";
import { GustoLogo, GustoIcon } from "@/components/GustoLogo";

const RecipePage = () => {
  const { id } = useParams();
  const { user, token } = useAuth();
  const [recipe, setRecipe] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewReproduced, setReviewReproduced] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [liking, setLiking] = useState(false);
  
  // Cooking mode state
  const [cookingMode, setCookingMode] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [showShoppingList, setShowShoppingList] = useState(false);
  const [cookingComplete, setCookingComplete] = useState(false);
  const [showCompletionReview, setShowCompletionReview] = useState(false);
  const [completionPhoto, setCompletionPhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [wineSuggestion, setWineSuggestion] = useState(null);
  const [loadingWineSuggestion, setLoadingWineSuggestion] = useState(false);
  const [invitationLink, setInvitationLink] = useState(null);
  const [creatingInvitation, setCreatingInvitation] = useState(false);
  const [mealStats, setMealStats] = useState(null);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const photoInputRef = useRef(null);

  // Shopping list functions
  const generateShoppingListText = useCallback(() => {
    if (!recipe?.ingredients?.length) return "";
    
    let text = `🛒 Liste de courses\n`;
    text += `📖 ${recipe.name}\n`;
    text += `👤 ${recipe.servings} parts\n`;
    text += `─────────────────\n\n`;
    
    recipe.ingredients.forEach((ing, idx) => {
      const qty = ing.quantity ? `${ing.quantity} ` : "";
      const unit = ing.unit ? `${ing.unit} ` : "";
      text += `☐ ${qty}${unit}${ing.name}\n`;
    });
    
    text += `\n─────────────────\n`;
    text += `Gusto 🍳`;
    
    return text;
  }, [recipe]);

  // Format Notes-compatible (simpler, checklist style)
  const generateNotesFormat = useCallback(() => {
    if (!recipe?.ingredients?.length) return "";
    
    let text = `Liste de courses - ${recipe.name}\n`;
    text += `${recipe.servings} personnes\n`;
    text += `\n`;
    
    recipe.ingredients.forEach((ing) => {
      const qty = ing.quantity ? `${ing.quantity}` : "";
      const unit = ing.unit ? ` ${ing.unit}` : "";
      const qtyUnit = qty || unit ? `${qty}${unit} - ` : "";
      text += `• ${qtyUnit}${ing.name}\n`;
    });
    
    return text;
  }, [recipe]);

  const copyShoppingList = async () => {
    const text = generateShoppingListText();
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Liste copiée ! Collez-la dans Notes 📝");
    } catch (err) {
      toast.error("Erreur lors de la copie");
    }
  };

  const openInNotes = async () => {
    const text = generateNotesFormat();
    
    // Try to open in notes app (works on iOS)
    const notesUrl = `mobilenotes://`;
    
    // First copy to clipboard
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Liste copiée ! Ouvrez Notes et collez 📝", {
        duration: 4000,
        action: {
          label: "OK",
          onClick: () => {}
        }
      });
      
      // Try to open notes app on mobile
      if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        window.location.href = notesUrl;
      }
    } catch (err) {
      // Fallback: just copy
      copyShoppingList();
    }
  };

  const shareShoppingList = async () => {
    const text = generateNotesFormat();
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Liste de courses - ${recipe.name}`,
          text: text,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyShoppingList();
        }
      }
    } else {
      copyShoppingList();
    }
  };

  const downloadShoppingList = () => {
    const text = generateShoppingListText();
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `liste-courses-${recipe.name.toLowerCase().replace(/\s+/g, '-')}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("Liste téléchargée !");
  };

  // Completion functions
  const finishCooking = async () => {
    stopSpeaking();
    setIsPlaying(false);
    setCookingComplete(true);
    
    // Speak congratulations
    if (audioEnabled) {
      setTimeout(() => {
        speakStep("Félicitations ! Votre plat est terminé. Bonne dégustation !");
      }, 500);
    }
    
    // Fetch wine suggestion from user's cellar
    if (token && id) {
      fetchWineSuggestion();
    }
  };

  const fetchWineSuggestion = async () => {
    setLoadingWineSuggestion(true);
    try {
      const response = await axios.get(`${API}/wine/suggest/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.suggestion) {
        setWineSuggestion(response.data.suggestion);
      }
    } catch (error) {
      console.log('No wine suggestion available');
    } finally {
      setLoadingWineSuggestion(false);
    }
  };

  const handleCompletionPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Convert to base64 for preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setCompletionPhoto(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const submitCompletionReview = async () => {
    if (!token) {
      toast.error("Connectez-vous pour laisser un avis");
      return;
    }
    
    setUploadingPhoto(true);
    try {
      // Submit review with reproduced = true
      const response = await axios.post(
        `${API}/recipes/${id}/reviews`,
        { 
          rating: reviewRating, 
          comment: reviewComment, 
          reproduced: true,
          completion_photo: completionPhoto 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setReviews(prev => [response.data, ...prev]);
      setRecipe(prev => ({
        ...prev,
        reproductions_count: (prev.reproductions_count || 0) + 1
      }));
      
      toast.success("Bravo ! Votre réalisation a été partagée 🎉");
      setShowCompletionReview(false);
      setCookingComplete(false);
      setCookingMode(false);
      setCompletionPhoto(null);
      setReviewComment("");
      setReviewRating(5);
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const exitCompletionScreen = () => {
    setCookingComplete(false);
    setCookingMode(false);
    setCompletionPhoto(null);
  };

  const createMealInvitation = async () => {
    if (!recipe || !user) return;
    setCreatingInvitation(true);
    try {
      const res = await axios.post(
        `${API}/meal-invitations`,
        {
          recipe_id: recipe.id,
          recipe_name: recipe.name,
          chef_name: user.pseudo,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const url = `${window.location.origin}/rate/${res.data.id}`;
      setInvitationLink(url);
      if (navigator.share) {
        try {
          await navigator.share({
            title: `Notez mon plat - ${recipe.name}`,
            text: `${user.pseudo} vous invite à noter son plat "${recipe.name}" sur Gusto !`,
            url,
          });
        } catch {
          // User cancelled share
        }
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Lien copié dans le presse-papier !");
      }
    } catch {
      toast.error("Erreur lors de la création du lien");
    } finally {
      setCreatingInvitation(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [recipeRes, reviewsRes, statsRes] = await Promise.all([
          axios.get(`${API}/recipes/${id}`),
          axios.get(`${API}/recipes/${id}/reviews`),
          axios.get(`${API}/recipes/${id}/meal-stats`).catch(() => ({ data: null }))
        ]);
        setRecipe(recipeRes.data);
        setReviews(reviewsRes.data);
        setLikesCount(recipeRes.data.likes_count || 0);
        if (statsRes.data) setMealStats(statsRes.data);
        
        // Check if user liked this recipe
        if (token) {
          try {
            const likeRes = await axios.get(`${API}/recipes/${id}/like`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setLiked(likeRes.data.liked);
          } catch (e) {
            // Ignore like check errors
          }
        }
      } catch (error) {
        console.error("Error fetching recipe:", error);
        toast.error("Recette introuvable");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, token]);

  // Like/Unlike recipe
  const handleLike = async () => {
    if (!token) {
      toast.error("Connectez-vous pour aimer cette recette");
      return;
    }
    
    setLiking(true);
    try {
      const response = await axios.post(
        `${API}/recipes/${id}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLiked(response.data.liked);
      setLikesCount(response.data.likes_count);
      if (response.data.liked) {
        toast.success("❤️ Ajouté aux favoris !");
      }
    } catch (error) {
      toast.error("Erreur");
    } finally {
      setLiking(false);
    }
  };

  // Timer for cooking mode
  useEffect(() => {
    if (isPlaying && cookingMode) {
      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [isPlaying, cookingMode]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Speak step using OpenAI TTS
  const speakStep = useCallback(async (text) => {
    if (!audioEnabled || !text) return;
    
    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    
    setLoadingAudio(true);
    setIsSpeaking(true);
    
    try {
      const response = await axios.post(`${API}/tts`, { 
        text,
        voice: "nova" // Warm, friendly voice
      });
      
      if (response.data.audio_base64) {
        const audio = new Audio(`data:audio/mp3;base64,${response.data.audio_base64}`);
        audioRef.current = audio;
        
        audio.onended = () => {
          setIsSpeaking(false);
          audioRef.current = null;
        };
        
        audio.onerror = () => {
          setIsSpeaking(false);
          audioRef.current = null;
        };
        
        await audio.play();
      }
    } catch (error) {
      console.error("TTS error:", error);
      // Fallback to browser TTS if OpenAI fails
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'fr-FR';
        utterance.rate = 0.9;
        utterance.onend = () => setIsSpeaking(false);
        speechSynthesis.speak(utterance);
      } else {
        setIsSpeaking(false);
      }
    } finally {
      setLoadingAudio(false);
    }
  }, [audioEnabled]);

  // Stop speaking
  const stopSpeaking = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    speechSynthesis.cancel();
    setIsSpeaking(false);
  }, []);

  const startCookingMode = () => {
    setCookingMode(true);
    setCurrentStepIndex(0);
    setElapsedTime(0);
    setIsPlaying(false);
    setIsSpeaking(false);
    setCookingComplete(false);
    setCompletionPhoto(null);
  };

  const exitCookingMode = () => {
    setCookingMode(false);
    setIsPlaying(false);
    setElapsedTime(0);
    setCookingComplete(false);
    setCompletionPhoto(null);
    stopSpeaking();
  };

  const togglePlay = async () => {
    if (!isPlaying && recipe?.steps?.[currentStepIndex]) {
      setIsPlaying(true);
      // Build a comprehensive description of the step
      const step = recipe.steps[currentStepIndex];
      let fullText = step.description;
      
      // Add ingredient info to speech
      if (step.ingredient) {
        fullText = `${step.ingredient.quantity || ''} ${step.ingredient.name}. ${step.description}`;
      }
      
      await speakStep(fullText);
    } else {
      setIsPlaying(false);
      stopSpeaking();
    }
  };

  const goToStep = async (index) => {
    if (index >= 0 && index < recipe.steps.length) {
      stopSpeaking();
      setCurrentStepIndex(index);
      
      if (isPlaying && audioEnabled) {
        const step = recipe.steps[index];
        let fullText = step.description;
        if (step.ingredient) {
          fullText = `${step.ingredient.quantity || ''} ${step.ingredient.name}. ${step.description}`;
        }
        // Small delay to ensure UI updates first
        setTimeout(() => speakStep(fullText), 100);
      }
    }
  };

  const toggleAudio = () => {
    if (audioEnabled) {
      stopSpeaking();
    }
    setAudioEnabled(!audioEnabled);
  };

  const submitReview = async () => {
    if (!token) {
      toast.error("Connectez-vous pour laisser un avis");
      return;
    }
    
    setSubmittingReview(true);
    try {
      const response = await axios.post(
        `${API}/recipes/${id}/reviews`,
        { rating: reviewRating, comment: reviewComment, reproduced: reviewReproduced },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReviews(prev => [response.data, ...prev]);
      setShowReviewDialog(false);
      setReviewComment("");
      setReviewRating(5);
      setReviewReproduced(false);
      toast.success("Avis ajouté !");
      
      if (reviewReproduced) {
        setRecipe(prev => ({
          ...prev,
          reproductions_count: (prev.reproductions_count || 0) + 1
        }));
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSubmittingReview(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="px-4 py-8 text-center">
        <GustoLogo size="lg" />
        <p className="text-muted-foreground">Recette introuvable</p>
        <Link to="/"><Button className="mt-4 rounded-full">Retour</Button></Link>
      </div>
    );
  }

  const avgRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  const totalTime = (recipe.prep_time || 0) + (recipe.cook_time || 0);
  const progressPercent = recipe.steps?.length > 0 
    ? ((currentStepIndex + 1) / recipe.steps.length) * 100 
    : 0;

  // Cooking Mode UI - Completely redesigned
  if (cookingMode) {
    const currentStep = recipe.steps?.[currentStepIndex];
    const hasMultipleIngredients = currentStep?.all_ingredients?.length > 0;
    const stepIngredients = hasMultipleIngredients 
      ? currentStep.all_ingredients 
      : (currentStep?.ingredient ? [currentStep.ingredient] : []);
    const utensils = currentStep?.all_utensils || (currentStep?.utensil ? [currentStep.utensil] : []);
    const isLastStep = currentStepIndex >= (recipe.steps?.length || 1) - 1;
    const allIngredients = recipe.ingredients || [];
    
    // Completion Screen
    if (cookingComplete) {
      return (
        <div className="fixed inset-0 bg-gradient-to-b from-charcoal to-gray-900 text-white z-50 flex flex-col" data-testid="cooking-complete">
          {/* Header */}
          <div className="flex-shrink-0 bg-charcoal/95 backdrop-blur-sm px-4 py-3 border-b border-white/10 safe-area-top">
            <div className="flex items-center justify-between max-w-4xl mx-auto">
              <Button 
                variant="ghost" 
                onClick={exitCompletionScreen} 
                className="text-white hover:bg-white/10 gap-2"
              >
                <X className="w-5 h-5" />
              </Button>
              <p className="font-heading font-bold text-sm sm:text-base">{recipe.name}</p>
              <div className="w-10" />
            </div>
          </div>

          {/* Completion Content */}
          <div className="flex-1 overflow-y-auto px-4 py-8">
            <div className="max-w-md mx-auto text-center space-y-6">
              {/* Celebration Animation */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", duration: 0.6 }}
                className="relative"
              >
                <div className="w-32 h-32 mx-auto rounded-full bg-gradient-to-br from-accent to-primary flex items-center justify-center">
                  <Trophy className="w-16 h-16 text-white" />
                </div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="absolute -top-4 -right-4"
                >
                  <PartyPopper className="w-10 h-10 text-accent" />
                </motion.div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="space-y-2"
              >
                <h2 className="font-heading text-3xl font-bold">Bravo ! 🎉</h2>
                <p className="text-xl text-white/80">Bonne dégustation !</p>
              </motion.div>

              {/* Recipe Final Image */}
              {recipe.final_image && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 }}
                  className="relative aspect-square max-w-xs mx-auto rounded-3xl overflow-hidden bg-white/10"
                >
                  <img 
                    src={recipe.final_image.startsWith('data:') ? recipe.final_image : `data:image/jpeg;base64,${recipe.final_image}`}
                    alt={recipe.name}
                    className="w-full h-full object-cover"
                  />
                </motion.div>
              )}

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="flex justify-center gap-6 py-4"
              >
                <div className="text-center">
                  <p className="text-2xl font-bold text-primary">{formatTime(elapsedTime)}</p>
                  <p className="text-xs text-white/60">Temps total</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-accent">{recipe.steps?.length || 0}</p>
                  <p className="text-xs text-white/60">Étapes</p>
                </div>
              </motion.div>

              {/* Wine Suggestion from Cellar */}
              {loadingWineSuggestion ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.65 }}
                  className="flex items-center justify-center gap-2 py-4"
                >
                  <Loader2 className="w-5 h-5 animate-spin text-red-400" />
                  <span className="text-white/60 text-sm">Recherche d'un vin dans votre cave...</span>
                </motion.div>
              ) : wineSuggestion ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.65 }}
                  className="mx-auto max-w-sm"
                >
                  <div className="bg-gradient-to-br from-red-900/40 to-rose-900/40 rounded-2xl p-4 border border-red-500/20">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center">
                        <Wine className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-red-300 font-medium">De votre cave</p>
                        <p className="text-white font-semibold text-sm">Suggestion d'accord</p>
                      </div>
                    </div>
                    
                    <div className="bg-black/20 rounded-xl p-3 mb-3">
                      <p className="text-white font-medium">{wineSuggestion.wine?.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-red-300 border-red-500/30 text-xs">
                          {wineSuggestion.wine?.type}
                        </Badge>
                        {wineSuggestion.wine?.vintage && (
                          <span className="text-white/60 text-xs">{wineSuggestion.wine?.vintage}</span>
                        )}
                        {wineSuggestion.wine?.appellation && (
                          <span className="text-white/60 text-xs">• {wineSuggestion.wine?.appellation}</span>
                        )}
                      </div>
                    </div>
                    
                    <p className="text-white/70 text-xs italic leading-relaxed">
                      "{wineSuggestion.reason}"
                    </p>
                    
                    <Link 
                      to="/wine-cellar" 
                      className="mt-3 flex items-center justify-center gap-2 text-red-300 hover:text-red-200 text-sm font-medium transition-colors"
                    >
                      Voir ma cave
                      <ArrowLeft className="w-4 h-4 rotate-180" />
                    </Link>
                  </div>
                </motion.div>
              ) : user && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.65 }}
                  className="text-center py-2"
                >
                  <Link 
                    to="/wine-cellar" 
                    className="inline-flex items-center gap-2 text-red-300 hover:text-red-200 text-sm transition-colors"
                  >
                    <Wine className="w-4 h-4" />
                    Ajouter un vin à ma cave pour des suggestions d'accords
                  </Link>
                </motion.div>
              )}

              {/* Photo Upload Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="space-y-4"
              >
                <p className="text-sm text-white/60">Partagez votre création !</p>
                
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleCompletionPhotoChange}
                  className="hidden"
                />

                {completionPhoto ? (
                  <div className="relative aspect-square max-w-xs mx-auto rounded-2xl overflow-hidden">
                    <img src={completionPhoto} alt="Ma création" className="w-full h-full object-cover" />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full"
                      onClick={() => setCompletionPhoto(null)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => photoInputRef.current?.click()}
                    className="w-full max-w-xs mx-auto rounded-2xl border-dashed border-2 border-white/30 bg-white/5 hover:bg-white/10 text-white h-32 flex flex-col gap-2"
                  >
                    <Camera className="w-8 h-8" />
                    <span>Prendre une photo de mon plat</span>
                  </Button>
                )}
              </motion.div>
            </div>
          </div>

          {/* Bottom Actions */}
          <div className="flex-shrink-0 bg-charcoal/95 backdrop-blur-sm border-t border-white/10 px-4 py-4 safe-area-bottom">
            <div className="max-w-md mx-auto space-y-3">
              {user ? (
                <>
                  <Button
                    onClick={() => setShowCompletionReview(true)}
                    className="w-full rounded-full bg-primary hover:bg-primary/90 h-14 text-lg gap-2"
                  >
                    <Star className="w-5 h-5" />
                    Laisser mon avis
                  </Button>
                  <Button
                    variant="outline"
                    onClick={createMealInvitation}
                    disabled={creatingInvitation}
                    className="w-full rounded-full border-white/30 text-white hover:bg-white/10 h-12 gap-2"
                    data-testid="invite-rate-btn"
                  >
                    {creatingInvitation ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Link2 className="w-4 h-4" />
                    )}
                    Inviter mes convives à noter
                  </Button>
                  {invitationLink && (
                    <div className="flex items-center gap-2 bg-white/10 rounded-xl px-3 py-2">
                      <p className="text-xs text-white/60 truncate flex-1">{invitationLink}</p>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-white hover:bg-white/20 flex-shrink-0"
                        onClick={() => {
                          navigator.clipboard.writeText(invitationLink);
                          toast.success("Lien copié !");
                        }}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <Button
                  onClick={exitCompletionScreen}
                  className="w-full rounded-full bg-primary hover:bg-primary/90 h-14 text-lg"
                >
                  Terminer
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={exitCompletionScreen}
                className="w-full text-white/60 hover:text-white"
              >
                Fermer
              </Button>
            </div>
          </div>

          {/* Completion Review Dialog */}
          <Dialog open={showCompletionReview} onOpenChange={setShowCompletionReview}>
            <DialogContent className="sm:max-w-md bg-charcoal text-white border-white/10">
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2 text-white">
                  <Star className="w-5 h-5 text-accent" />
                  Mon avis
                </DialogTitle>
                <DialogDescription className="text-white/60">
                  Partagez votre expérience avec {recipe.name}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Rating */}
                <div className="space-y-2">
                  <Label className="text-white/80">Note</Label>
                  <div className="flex gap-2 justify-center">
                    {[1, 2, 3, 4, 5].map((rating) => (
                      <button
                        key={rating}
                        onClick={() => setReviewRating(rating)}
                        className="p-1 hover:scale-110 transition-transform"
                      >
                        <Star className={`w-10 h-10 ${rating <= reviewRating ? 'text-accent fill-accent' : 'text-white/30'}`} />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Photo Preview */}
                {completionPhoto && (
                  <div className="aspect-video rounded-xl overflow-hidden bg-white/10">
                    <img src={completionPhoto} alt="Ma création" className="w-full h-full object-cover" />
                  </div>
                )}

                {/* Comment */}
                <div className="space-y-2">
                  <Label className="text-white/80">Commentaire</Label>
                  <Textarea
                    value={reviewComment}
                    onChange={(e) => setReviewComment(e.target.value)}
                    placeholder="Comment s'est passée votre réalisation ?"
                    className="rounded-xl resize-none bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    rows={3}
                  />
                </div>
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button 
                  variant="ghost" 
                  onClick={() => setShowCompletionReview(false)} 
                  className="rounded-full text-white/60"
                >
                  Annuler
                </Button>
                <Button 
                  onClick={submitCompletionReview} 
                  disabled={uploadingPhoto}
                  className="rounded-full bg-accent hover:bg-accent/90 flex-1"
                >
                  {uploadingPhoto ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Envoi...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Publier ma réalisation
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      );
    }
    
    // Normal Cooking Mode (Steps)
    return (
      <div className="fixed inset-0 bg-gradient-to-b from-charcoal to-gray-900 text-white z-50 flex flex-col" data-testid="cooking-mode">
        {/* Header - Fixed at top */}
        <div className="flex-shrink-0 bg-charcoal/95 backdrop-blur-sm px-4 py-3 border-b border-white/10 safe-area-top">
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            <Button 
              variant="ghost" 
              onClick={exitCookingMode} 
              className="text-white hover:bg-white/10 gap-2"
            >
              <X className="w-5 h-5" />
              <span className="hidden sm:inline">Quitter</span>
            </Button>
            
            <div className="text-center flex-1 mx-4">
              <p className="font-heading font-bold text-sm sm:text-base truncate">{recipe.name}</p>
              <p className="text-xs text-white/60">Étape {currentStepIndex + 1} sur {recipe.steps?.length || 0}</p>
            </div>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleAudio}
              className={`text-white hover:bg-white/10 ${!audioEnabled && 'opacity-50'}`}
            >
              {audioEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </Button>
          </div>
          <Progress value={progressPercent} className="mt-2 h-1.5 bg-white/20" />
        </div>

        {/* Main Content - Scrollable with Side Ingredients */}
        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col lg:flex-row max-w-6xl mx-auto">
            {/* Main Step Content */}
            <div className="flex-1 px-4 py-6">
              <div className="max-w-2xl mx-auto">
                <AnimatePresence mode="wait">
                  {currentStep && (
                    <motion.div
                      key={currentStepIndex}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="space-y-6"
                    >
                      {/* Step Type & Action Badges */}
                      <div className="flex justify-center gap-2 flex-wrap">
                        <Badge className={`text-sm px-4 py-1 ${
                          currentStep.step_type === 'cuisson' ? 'bg-orange-500' :
                          currentStep.step_type === 'dressage' ? 'bg-purple-500' :
                          'bg-green-500'
                        }`}>
                          {currentStep.step_type === 'cuisson' ? '🔥 Cuisson' :
                           currentStep.step_type === 'dressage' ? '🍽️ Dressage' :
                           '🥗 Préparation'}
                        </Badge>
                        {currentStep.action && (
                          <Badge className="bg-blue-500/80 text-sm px-4 py-1">
                            {currentStep.action}
                          </Badge>
                        )}
                      </div>

                      {/* Main Instruction */}
                      <div className="bg-white/5 rounded-3xl p-6 backdrop-blur-sm border border-white/10">
                        <p className="text-xl sm:text-2xl md:text-3xl font-heading leading-relaxed text-center">
                          {currentStep.description}
                        </p>
                        
                        {/* Speaking Indicator */}
                        {(isSpeaking || loadingAudio) && (
                          <div className="flex items-center justify-center gap-2 mt-4 text-primary">
                            {loadingAudio ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Volume2 className="w-4 h-4 animate-pulse" />
                            )}
                            <span className="text-sm">{loadingAudio ? 'Chargement...' : 'Lecture en cours...'}</span>
                          </div>
                        )}
                      </div>

                      {/* Step Ingredients (Mobile) */}
                      {stepIngredients.length > 0 && (
                        <div className="lg:hidden space-y-3">
                          <h3 className="text-sm font-medium text-white/60 text-center">
                            🥘 Ingrédient{stepIngredients.length > 1 ? 's' : ''} de cette étape
                          </h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {stepIngredients.map((ing, idx) => (
                              <motion.div
                                key={idx}
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: idx * 0.1 }}
                                className="bg-primary/20 rounded-2xl p-4 flex items-center gap-4 border border-primary/30"
                              >
                                {ing.image_url ? (
                                  <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-white/10">
                                    <img src={ing.image_url} alt={ing.name} className="w-full h-full object-cover" />
                                  </div>
                                ) : (
                                  <div className="w-16 h-16 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                                    <span className="text-2xl">🥘</span>
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="font-bold text-lg truncate">{ing.name}</p>
                                  {(ing.quantity || ing.unit) && (
                                    <p className="text-primary text-sm font-medium">{ing.quantity} {ing.unit}</p>
                                  )}
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Utensils Section */}
                      {utensils.length > 0 && (
                        <div className="space-y-3">
                          <h3 className="text-sm font-medium text-white/60 text-center">
                            🍳 Ustensile{utensils.length > 1 ? 's' : ''} nécessaire{utensils.length > 1 ? 's' : ''}
                          </h3>
                          <div className="flex flex-wrap justify-center gap-2">
                            {utensils.map((utensil, idx) => (
                              <Badge 
                                key={idx}
                                className="bg-secondary/30 text-white border-secondary/50 text-sm px-4 py-2"
                              >
                                🍳 {typeof utensil === 'string' ? utensil : utensil.name}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Step Media (if any photo was taken) */}
                      {currentStep.media_urls?.length > 0 && (
                        <div className="flex justify-center">
                          <div className="w-48 h-48 rounded-2xl overflow-hidden bg-white/10">
                            <img 
                              src={currentStep.media_urls[0].startsWith('data:') 
                                ? currentStep.media_urls[0] 
                                : `${API}/media/${currentStep.media_urls[0]}`
                              }
                              alt="Étape"
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>
                      )}

                      {/* Step Video (if any) */}
                      {currentStep.video_url && (
                        <div className="flex justify-center">
                          <div className="w-64 rounded-2xl overflow-hidden bg-white/10">
                            <video 
                              src={currentStep.video_url}
                              controls
                              className="w-full"
                              playsInline
                            />
                          </div>
                        </div>
                      )}

                      {/* Timer */}
                      <div className="flex justify-center">
                        <div className="bg-white/10 rounded-full px-6 py-2 flex items-center gap-2">
                          <Clock className="w-4 h-4 text-white/60" />
                          <span className="font-mono text-lg">{formatTime(elapsedTime)}</span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Side Panel - All Ingredients (Desktop) */}
            {allIngredients.length > 0 && (
              <div className="hidden lg:block w-80 border-l border-white/10 bg-white/5">
                <div className="sticky top-0 p-4">
                  <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
                    <Utensils className="w-5 h-5 text-primary" />
                    Tous les ingrédients
                  </h3>
                  <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-2">
                    {allIngredients.map((ing, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-3 p-2 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
                      >
                        {ing.image_url ? (
                          <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                            <img src={ing.image_url} alt={ing.name} className="w-full h-full object-cover" />
                          </div>
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-xl">🥘</span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{ing.name}</p>
                          <p className="text-xs text-primary">{ing.quantity} {ing.unit}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Controls - Fixed at bottom, above any system UI */}
        <div className="flex-shrink-0 bg-charcoal/95 backdrop-blur-sm border-t border-white/10 px-4 py-4 safe-area-bottom">
          <div className="max-w-md mx-auto space-y-4">
            {/* Main Controls */}
            <div className="flex items-center justify-center gap-6">
              <Button
                variant="ghost"
                size="icon"
                className="w-14 h-14 rounded-full text-white hover:bg-white/10 disabled:opacity-30"
                onClick={() => goToStep(currentStepIndex - 1)}
                disabled={currentStepIndex === 0}
              >
                <SkipBack className="w-7 h-7" />
              </Button>
              
              {isLastStep ? (
                <Button
                  size="icon"
                  className="w-20 h-20 rounded-full bg-accent hover:bg-accent/90 shadow-lg shadow-accent/30"
                  onClick={finishCooking}
                >
                  <Check className="w-10 h-10" />
                </Button>
              ) : (
                <Button
                  size="icon"
                  className="w-20 h-20 rounded-full bg-primary hover:bg-primary/90 shadow-lg shadow-primary/30"
                  onClick={togglePlay}
                  disabled={loadingAudio}
                >
                  {loadingAudio ? (
                    <Loader2 className="w-8 h-8 animate-spin" />
                  ) : isPlaying ? (
                    <Pause className="w-10 h-10" />
                  ) : (
                    <Play className="w-10 h-10 ml-1" />
                  )}
                </Button>
              )}
              
              <Button
                variant="ghost"
                size="icon"
                className="w-14 h-14 rounded-full text-white hover:bg-white/10 disabled:opacity-30"
                onClick={isLastStep ? finishCooking : () => goToStep(currentStepIndex + 1)}
                disabled={false}
              >
                {isLastStep ? (
                  <Trophy className="w-7 h-7 text-accent" />
                ) : (
                  <SkipForward className="w-7 h-7" />
                )}
              </Button>
            </div>
            
            {/* Finish Button on Last Step */}
            {isLastStep && (
              <Button
                onClick={finishCooking}
                className="w-full rounded-full bg-accent hover:bg-accent/90 h-12 gap-2"
              >
                <PartyPopper className="w-5 h-5" />
                J'ai terminé !
              </Button>
            )}
            
            {/* Step Navigation Dots */}
            <div className="flex justify-center gap-2 flex-wrap px-4">
              {recipe.steps?.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goToStep(i)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === currentStepIndex 
                      ? 'bg-primary w-8' 
                      : i < currentStepIndex 
                        ? 'bg-primary/50 w-2 hover:bg-primary/70' 
                        : 'bg-white/30 w-2 hover:bg-white/50'
                  }`}
                  aria-label={`Aller à l'étape ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Normal Recipe View
  return (
    <div className="pb-8" data-testid="recipe-page">
      {/* Hero Image */}
      <div className="relative aspect-[4/3] md:aspect-video bg-gradient-to-br from-primary/20 to-accent/30">
        {recipe.final_image ? (
          <img 
            src={recipe.final_image.startsWith('data:') ? recipe.final_image : `data:image/jpeg;base64,${recipe.final_image}`}
            alt={recipe.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <GustoLogo size="lg" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        
        <Link 
          to="/" 
          className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-md hover:bg-white"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>

        {/* Like button */}
        <Button
          variant="ghost"
          onClick={handleLike}
          disabled={liking}
          className={`absolute top-4 right-4 w-12 h-12 rounded-full shadow-md ${
            liked ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-white/90 hover:bg-white text-charcoal'
          }`}
        >
          <Heart className={`w-6 h-6 ${liked ? 'fill-white' : ''}`} />
        </Button>

        {/* Start cooking button */}
        {recipe.steps?.length > 0 && (
          <Button
            onClick={startCookingMode}
            className="absolute bottom-4 right-4 bg-primary hover:bg-primary/90 rounded-full shadow-xl"
            data-testid="start-cooking-btn"
          >
            <Play className="w-4 h-4 mr-2" />
            Lancer la recette
          </Button>
        )}
      </div>

      <div className="px-4 -mt-6 relative z-10">
        {/* Recipe Info Card */}
        <Card className="shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-charcoal flex-1">{recipe.name}</h1>
              
              <div className="flex items-center gap-3">
                {/* Edit button for owner */}
                {user?.id === recipe.chef_id && (
                  <Link to={`/recipe/${id}/edit`}>
                    <Button variant="outline" size="sm" className="rounded-full gap-1">
                      <Edit2 className="w-4 h-4" />
                      <span className="hidden sm:inline">Modifier</span>
                    </Button>
                  </Link>
                )}
                
                {/* Likes counter */}
                <div className="flex items-center gap-1 text-red-500">
                  <Heart className={`w-5 h-5 ${likesCount > 0 ? 'fill-red-500' : ''}`} />
                  <span className="font-bold">{likesCount}</span>
                </div>
              </div>
            </div>
            
            <Link 
              to={`/chef/${recipe.chef_id}`} 
              className="inline-flex items-center gap-2 mt-3 hover:opacity-80 transition-opacity"
            >
              <ChefAvatar
                src={recipe.chef_avatar}
                name={recipe.chef_pseudo}
                size="sm"
                showToque={true}
              />
              <span className="font-medium text-primary">Chef(fe) {recipe.chef_pseudo}</span>
            </Link>

            {recipe.description && (
              <p className="text-muted-foreground mt-4">{recipe.description}</p>
            )}

            <div className="flex flex-wrap items-center gap-4 mt-4">
              <div className="flex items-center gap-1 text-sm">
                <Clock className="w-4 h-4 text-primary" />
                <span>{totalTime} min</span>
              </div>
              <div className="flex items-center gap-1 text-sm">
                <Users className="w-4 h-4 text-primary" />
                <span>{recipe.servings} parts</span>
              </div>
              <div className="flex items-center gap-1 text-sm">
                <Copy className="w-4 h-4 text-secondary" />
                <span>{recipe.reproductions_count || 0} reproductions</span>
              </div>
              {avgRating && (
                <div className="flex items-center gap-1 text-sm">
                  <Star className="w-4 h-4 text-accent fill-accent" />
                  <span>{avgRating} ({reviews.length})</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Meal Stats - Ratings from guests */}
        {mealStats && mealStats.ratings_count > 0 && (
          <Card className="mt-4 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200" data-testid="meal-stats-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-5 h-5 text-amber-600" />
                <h3 className="font-heading font-semibold text-charcoal">Notes des convives</h3>
                <Badge className="bg-amber-100 text-amber-700 text-xs">
                  {mealStats.meals_count} repas
                </Badge>
              </div>
              <div className="flex items-center gap-4 mb-3">
                <div className="text-center">
                  <p className="font-heading text-3xl font-bold text-amber-600">{mealStats.avg_overall}</p>
                  <p className="text-xs text-muted-foreground">/5</p>
                </div>
                <div className="flex-1 grid grid-cols-2 gap-2">
                  {[
                    { label: "Goût", value: mealStats.avg_taste, emoji: "👅" },
                    { label: "Équilibre", value: mealStats.avg_balance, emoji: "⚖️" },
                    { label: "Satiété", value: mealStats.avg_satiety, emoji: "🍽️" },
                    { label: "Présentation", value: mealStats.avg_presentation, emoji: "🎨" },
                  ].map((c) => (
                    <div key={c.label} className="flex items-center gap-1.5 text-sm">
                      <span>{c.emoji}</span>
                      <span className="text-muted-foreground">{c.label}</span>
                      <span className="font-semibold text-charcoal ml-auto">{c.value}</span>
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {mealStats.ratings_count} note{mealStats.ratings_count > 1 ? "s" : ""} de convives
              </p>
            </CardContent>
          </Card>
        )}

        {/* Ingredients */}
        {recipe.ingredients?.length > 0 && (
          <section className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-semibold text-charcoal flex items-center gap-2">
                <Utensils className="w-5 h-5 text-primary" />
                Ingrédients ({recipe.ingredients.length})
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowShoppingList(true)}
                className="rounded-full gap-2"
              >
                <ShoppingCart className="w-4 h-4" />
                <span className="hidden sm:inline">Liste courses</span>
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {recipe.ingredients.map((ing, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="overflow-hidden h-full">
                    <div className="aspect-square bg-muted relative">
                      {ing.image_url ? (
                        <img src={ing.image_url} alt={ing.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-accent/10">
                          <span className="text-3xl">🥘</span>
                        </div>
                      )}
                    </div>
                    <CardContent className="p-3">
                      <p className="font-medium text-sm text-charcoal">{ing.name}</p>
                      <p className="text-xs text-primary font-bold">{ing.quantity} {ing.unit}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* Steps */}
        {recipe.steps?.length > 0 && (
          <section className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-semibold text-charcoal">
                Étapes ({recipe.steps.length})
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={startCookingMode}
                className="rounded-full"
              >
                <Play className="w-4 h-4 mr-1" />
                Mode cuisine
              </Button>
            </div>
            <div className="space-y-4">
              {recipe.steps.map((step, index) => (
                <motion.div
                  key={step.id || index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="border-l-4 border-l-primary">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold flex-shrink-0">
                          {index + 1}
                        </div>
                        
                        {(step.ingredient?.image_url || step.utensil_image_url) && (
                          <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
                            <img 
                              src={step.ingredient?.image_url || step.utensil_image_url}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <Badge variant="outline" className="text-xs">
                              {formatTime(step.timestamp)}
                            </Badge>
                            <Badge 
                              className={`text-xs ${
                                step.step_type === 'cuisson' ? 'bg-orange-100 text-orange-700' :
                                step.step_type === 'dressage' ? 'bg-purple-100 text-purple-700' :
                                'bg-green-100 text-green-700'
                              }`}
                              variant="outline"
                            >
                              {step.step_type}
                            </Badge>
                            {step.action && (
                              <Badge className="text-xs bg-blue-100 text-blue-700" variant="outline">
                                {step.action}
                              </Badge>
                            )}
                          </div>
                          <p className="text-charcoal">{step.description}</p>
                          
                          <div className="flex flex-wrap gap-2 mt-2">
                            {step.ingredient && (
                              <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full font-medium">
                                🥘 {step.ingredient.quantity} {step.ingredient.name}
                              </span>
                            )}
                            {step.utensil && (
                              <span className="text-xs bg-secondary/10 text-secondary px-2 py-1 rounded-full font-medium">
                                🍳 {step.utensil}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* Reviews */}
        <section className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-semibold text-charcoal">Avis ({reviews.length})</h2>
            <div className="flex items-center gap-2">
              {user && recipe?.chef_id === user?.id && recipe?.status === "finished" && (
                <Button
                  onClick={createMealInvitation}
                  disabled={creatingInvitation}
                  variant="outline"
                  className="rounded-full"
                  data-testid="invite-rate-detail-btn"
                >
                  {creatingInvitation ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Link2 className="w-4 h-4 mr-2" />
                  )}
                  Inviter à noter
                </Button>
              )}
              {user && (
                <Button onClick={() => setShowReviewDialog(true)} variant="outline" className="rounded-full">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Avis
                </Button>
              )}
            </div>
          </div>

          {invitationLink && (
            <Card className="mb-4 bg-primary/5 border-primary/20">
              <CardContent className="p-3 flex items-center gap-2">
                <Link2 className="w-4 h-4 text-primary flex-shrink-0" />
                <p className="text-xs text-muted-foreground truncate flex-1">{invitationLink}</p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-shrink-0"
                  onClick={() => {
                    navigator.clipboard.writeText(invitationLink);
                    toast.success("Lien copié !");
                  }}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          )}

          {reviews.length === 0 ? (
            <Card className="p-6 text-center">
              <MessageSquare className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground">Aucun avis</p>
              {user && (
                <Button onClick={() => setShowReviewDialog(true)} className="mt-3 rounded-full bg-primary">
                  Premier avis
                </Button>
              )}
            </Card>
          ) : (
            <div className="space-y-4">
              {reviews.map((review, index) => (
                <Card key={review.id} className="overflow-hidden">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <ChefAvatar
                          src={review.user_avatar}
                          name={review.user_pseudo}
                          size="sm"
                          showToque={true}
                        />
                        <div>
                          <p className="font-medium text-sm">{review.user_pseudo}</p>
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className={`w-3 h-3 ${i < review.rating ? 'text-accent fill-accent' : 'text-muted'}`} />
                            ))}
                          </div>
                        </div>
                      </div>
                      {review.reproduced && (
                        <Badge className="bg-secondary/20 text-secondary border-0">
                          <Check className="w-3 h-3 mr-1" />
                          Reproduit
                        </Badge>
                      )}
                    </div>
                    
                    {/* Review photo */}
                    {review.completion_photo && (
                      <div className="mt-3 rounded-xl overflow-hidden">
                        <img 
                          src={review.completion_photo.startsWith('data:') ? review.completion_photo : `data:image/jpeg;base64,${review.completion_photo}`}
                          alt={`Réalisation de ${review.user_pseudo}`}
                          className="w-full max-h-64 object-cover"
                        />
                      </div>
                    )}
                    
                    {review.comment && (
                      <p className="mt-3 text-sm text-muted-foreground">{review.comment}</p>
                    )}
                    
                    {/* Review date */}
                    <p className="mt-2 text-xs text-muted-foreground/60">
                      {new Date(review.created_at).toLocaleDateString('fr-FR', { 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Votre avis</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Note</Label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    onClick={() => setReviewRating(rating)}
                    className="p-1 hover:scale-110 transition-transform"
                  >
                    <Star className={`w-8 h-8 ${rating <= reviewRating ? 'text-accent fill-accent' : 'text-muted'}`} />
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Commentaire</Label>
              <Textarea
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder="Votre expérience..."
                className="rounded-xl resize-none"
                rows={3}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="reproduced" 
                checked={reviewReproduced}
                onCheckedChange={setReviewReproduced}
              />
              <Label htmlFor="reproduced" className="text-sm cursor-pointer">
                J'ai reproduit cette recette
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReviewDialog(false)} className="rounded-full">
              Annuler
            </Button>
            <Button onClick={submitReview} disabled={submittingReview} className="rounded-full bg-primary">
              {submittingReview ? "Envoi..." : "Publier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shopping List Dialog */}
      <Dialog open={showShoppingList} onOpenChange={setShowShoppingList}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-primary" />
              Liste de courses
            </DialogTitle>
            <DialogDescription>
              {recipe?.name} • {recipe?.servings} parts
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {/* Ingredients List */}
            <div className="bg-muted/50 rounded-xl p-4 max-h-[300px] overflow-y-auto">
              <ul className="space-y-2">
                {recipe?.ingredients?.map((ing, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-sm">
                    <div className="w-5 h-5 rounded border-2 border-primary/30 flex-shrink-0" />
                    <span className="flex-1">
                      {ing.quantity && <span className="font-medium text-primary">{ing.quantity} </span>}
                      {ing.unit && <span className="text-muted-foreground">{ing.unit} </span>}
                      <span className="text-charcoal">{ing.name}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Empty state */}
            {(!recipe?.ingredients || recipe.ingredients.length === 0) && (
              <div className="text-center py-8 text-muted-foreground">
                <ShoppingCart className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>Aucun ingrédient dans cette recette</p>
              </div>
            )}
          </div>

          <DialogFooter className="flex-col gap-2">
            {/* Main action - Open in Notes */}
            <Button 
              onClick={openInNotes}
              className="w-full rounded-full gap-2 bg-primary h-12"
            >
              <FileText className="w-5 h-5" />
              Copier pour Notes
            </Button>
            
            <div className="flex gap-2 w-full">
              <Button 
                variant="outline" 
                onClick={shareShoppingList}
                className="rounded-full flex-1 gap-2"
              >
                <Share2 className="w-4 h-4" />
                Partager
              </Button>
              <Button 
                variant="outline" 
                onClick={downloadShoppingList}
                className="rounded-full flex-1 gap-2"
              >
                <Download className="w-4 h-4" />
                Fichier .txt
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecipePage;
